import { useEffect, useState } from 'react';
import useCourseApi from 'Hooks/useCourseApi';
import useTranslation from 'Hooks/useTranslation';
import useCourseEmployeesReportApi from 'Hooks/useCourseEmployeesReportApi';
import { EmployeesByCourseReportFilterI } from 'types';
import { reportsApi } from 'Api/axios/reports-api';
import coursesApi from 'Api/axios/courses-api';
import { downloadFile } from 'Utils/file-utils';
import { toast } from 'sonner';

const useCourseEmployeesPage = (courseId: string) => {
  const { t2 } = useTranslation('course_employees_report');

  const [currentFilters, setCurrentFilters] = useState<EmployeesByCourseReportFilterI>({});

  /**
   * Custom hook para obtener los datos de los empleados de un curso.
   */
  const {
    data: courseEmployees,
    totals,
    fetchCourseEmployees,
    fetchCourseEmployeesTotals,
  } = useCourseEmployeesReportApi({
    courseId,
    init: false,
  });

  /**
   * Custom hook para obtener los datos de un curso.
   */
  const { course, fetchCourseById } = useCourseApi();

  /**
   * Realiza la petición para obtener nuevos datos de los empleados de un curso.
   */
  const onSearch = (filters: EmployeesByCourseReportFilterI) => {
    setCurrentFilters(filters);
    fetchCourseEmployees(courseId, filters);
  };

  /**
   * Descarga el certificado de un empleado de un curso.
   * @param courseId - El ID del curso.
   * @param employeeId - El ID del empleado.
   * @param certificationId - El ID de la certificación.
   */
  const onDownloadCertificate = async (courseId: string, employeeId: string, certificationId: string) => {
    const response = await coursesApi.downloadCertificate(courseId, employeeId, certificationId);
    if (response.data) {
      const employee = courseEmployees.find(employee => employee.id === employeeId);
      downloadFile(response.data, `${t2('certificate')}_${course?.name}_${employee?.fullName}.pdf`);
      toast.success(t2('certificate_downloaded'));
    }
  };

  /**
   * Exporta los datos de los empleados de un curso a un archivo Excel.
   */
  const onExportExcel = async () => {
    const response = await reportsApi.exportEmployeesByCourse(courseId, currentFilters);
    if (response.data) {
      downloadFile(response.data, `${t2('course_employees')}_${course?.name}.xlsx`);
      toast.success(t2('course_employees_exported'));
    }
  };

  /**
   * Obtiene el curso al inicializar el hook.
   */
  useEffect(() => {
    fetchCourseById(courseId);
    fetchCourseEmployees(courseId);
    fetchCourseEmployeesTotals(courseId);
  }, [courseId]);

  return {
    t2,
    course,
    totals,
    onSearch,
    onExportExcel,
    courseEmployees,
    onDownloadCertificate,
  };
};

export default useCourseEmployeesPage;
